import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import HeroImage from '../assets/herosec_image.80521ffd.jpg';
import CBTImage from '../assets/cbt-img3.a94b45f0.jpg';
import ThoughtsBookCover from '../assets/thoughts_book_cover.444d3ee2.jpg';
import TwinBrainBookCover from '../assets/twin_brain_book_cover.fbe0d9e5.jpg';
import CBTImage2 from '../assets/cbt-image-2.png';
import CBTImage3 from '../assets/cbt-image-3.png';
import CTABackground from '../assets/cbt-cta-bg.png';

const CBTServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const cbtConditions = [
    { name: "Depression", description: "Evidence-based treatment for depressive symptoms" },
    { name: "Health Anxiety", description: "Specialized approaches for health-related anxiety" },
    { name: "Specific Phobias", description: "Targeted treatment for specific fears" },
    { name: "Panic", description: "Effective interventions for panic attacks" },
    { name: "Generalised Anxiety Disorder", description: "Comprehensive anxiety management" },
    { name: "Social Anxiety", description: "Building confidence in social situations" },
    { name: "Obsessive Compulsive Disorder (OCD)", description: "Specialized OCD treatment protocols" },
    { name: "Post Traumatic Stress Disorder (PTSD)", description: "Trauma-informed CBT approaches" },
    { name: "And More…", description: "Other mental health conditions" }
  ];

  const testimonials = [
    {
      text: "Thank you for helping my daughter Samantha to go adolescence to recover from the unsuccessful attempts to find a friend from the past companies. Through therapeutic support, my daughter and I trust and support each other. Samantha has friends and they have a very good relationship, my daughter, do not use and others, involve in illicit affairs.",
      author: "Melanie Portman, housewife"
    },
    {
      text: "My wife wrote down my leadership courses. I treated them fairly skeptical. But I must admit, I liked the methods and attitude of Healthcity. I can say that has left some children's fears and complexes and now I can be more confident and efficient at home and at work.",
      author: "George Griffin, insurance manager"
    },
    {
      text: "Our family wants to thank Dr Smith for their concern and support, we helped to restore normal life after moving and illness. Children could join the new team at the school and to make friends. We could hardly used this time to live so quietly and smoothly without his help.",
      author: "Robert and Alicia Jones"
    }
  ];

  const assessmentQuestions = [
    {
      id: 1,
      question: "I have stress, anxiety or other psychological conditions in my personal life, my family life, workplace, employment, business etc."
    },
    {
      id: 2,
      question: "I am not good enough. Or I feel that I am a failure or an embarrassment to myself or to my family."
    },
    {
      id: 3,
      question: "I can't stop thinking about what is bothering me. Or my mind keeps running around a million miles in my head or my thinking goes round like a vicious cycle."
    },
    {
      id: 4,
      question: "I need coping techniques to deal with my difficulties"
    },
    {
      id: 5,
      question: "I want a safe space to talk how I am feeling and thinking about my difficulty."
    },
    {
      id: 6,
      question: "I have multiple difficulties to address, including childhood and current issues, abuses, bullying, trauma, trust, don't believe myself, I am worthless, low self-esteem etc"
    }
  ];

  const faqItems = [
    {
      question: "What Is Cognitive Behavior Therapy (CBT)?",
      answer: "CBT is a form of psychological treatment that has been demonstrated to be effective for a range of problems including depression, anxiety disorders, alcohol and drug use problems, marital problems, eating disorders and severe mental illness."
    },
    {
      question: "How long does CBT typically last?",
      answer: "CBT is typically a short-term therapy, lasting between 12-20 sessions. The exact number depends on the individual and the specific issues being addressed."
    },
    {
      question: "What can I expect during my first session?",
      answer: "Your first session will involve an assessment where we discuss your current difficulties, background, and goals for therapy. This helps us develop a personalized treatment plan."
    },
    {
      question: "Is CBT suitable for everyone?",
      answer: "CBT is effective for many people, but it requires active participation and commitment. We'll assess your suitability during the initial consultation."
    },
    {
      question: "What are some common techniques used in Cognitive Behavioral Therapy?",
      answer: "Common techniques include thought challenging, behavioral experiments, exposure therapy, activity scheduling, and homework assignments between sessions."
    },
    {
      question: "How can I find Cognitive Behavioral Therapy near me?",
      answer: "HealthCity offers both in-person and online CBT sessions. Contact us to book an assessment or consultation."
    },
    {
      question: "What is involved in CBT training?",
      answer: "CBT training involves extensive theoretical learning, practical skills development, supervised practice, and ongoing professional development to maintain competency."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[600px] flex items-end bg-cover bg-center" 
               style={{
                 backgroundImage: `url(${HeroImage})`
               }}>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-0">
          <div className="flex flex-col lg:flex-row items-end gap-8 lg:gap-12">
            {/* Left Content Card - Positioned at bottom */}
            <div className="lg:w-1/2 w-full relative mb-0">
              <div className="bg-slate-700/50 backdrop-blur-sm p-8 md:p-10 lg:p-12 rounded-t-lg shadow-lg">
                <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Cognitive Behavioral Therapy (CBT)
                </h1>
                <p className="text-sm md:text-base text-white/90 leading-relaxed mb-8">
                  Evidence-based treatment for depression, anxiety, and a wide range of mental health conditions. Transform your thoughts, change your life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-16">
                  <button onClick={() => navigate('/book-now')} className="bg-white hover:bg-gray-100 text-green-600 px-6 py-3 rounded font-medium transition-colors">
                    BOOK NOW
                  </button>
                </div>
              </div>
              {/* Arrow button positioned at bottom right corner of card */}
              <button onClick={() => navigate('/book-now')} className="absolute bottom-0 right-0 bg-green-600 hover:bg-green-700 text-white p-3 rounded-tl-lg transition-colors shadow-lg">
                <ArrowDown className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is CBT Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Text Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Cognitive Behavioral Therapy For A Range Of Mental Health Problems
              </h2>
              
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                CBT uses proven practical techniques to resolve many issues by changing the way you think, feel and behave.
              </p>
              
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                CBT helps to examine and explore 'the relationships between thoughts, feelings', bodily reactions 'and behaviors'. 
                'By exploring patterns' of unhelpful or irrational 'thinking that are leading to and keeping unhelpful behaviours 
                'and the beliefs that underline these unhelpful thinking, people with' poor mental health' may challenge and adjust 
                their unhelpful thinking and beliefs to help them improve their coping with difficult situations.
              </p>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Examples of unhelpful thoughts and beliefs in CBT:
                </h3>
                <ul className="space-y-2">
                  <li className="text-gray-700">• A depressed person may believe, "I am worthless."</li>
                  <li className="text-gray-700">• A person with panic disorder may believe, "I am in danger."</li>
                </ul>
              </div>

              {/* First Image */}
              <div className="mb-6">
                <img 
                  src={CBTImage3} 
                  alt="Person in therapy session" 
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            </div>
            
            {/* Right Column - How CBT Works */}
            <div>
              {/* Second Image */}
              <div className="mb-6">
                <img 
                  src={CBTImage2} 
                  alt="CBT therapy consultation" 
                  className="w-full rounded-lg shadow-md"
                />
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  CBT encourages people to view irrational 'beliefs as hypotheses' and not facts. 
                  This gives people a chance 'to test out' their irrational 'beliefs by doing an experiment.
                </p>
                
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  CBT requires the therapist and the client to 'work together' to set goals, 
                  solve problems and set homework to be completed between the sessions.
                </p>

                <div className="mb-4">
                  <p className="text-base text-gray-700 leading-relaxed mb-2">
                    Studies have shown that CBT:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Changes brain activity and improves brain functioning</li>
                    <li>• Is as useful as antidepressant medications for some people with depression</li>
                    <li>• Clients who receive CBT in addition to medication have better outcomes</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 italic">
                  Source/Acknowledged: Ken Duckworth, M.D., and Jacob L. Freedman, M.D., July 2012
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer For CBT Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">What We Offer For CBT</h2>
          
          <p className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto">
            At Health City, we provide both in-person and online Cognitive Behavioral Therapy (CBT) appointments. 
            We specialize in treating a wide range of mental health conditions, including:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {cbtConditions.map((condition, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 text-left">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{condition.name}</h3>
                <p className="text-gray-700">{condition.description}</p>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Our evidence-based therapy is designed to help individuals manage their thoughts and behaviors 
            for improved emotional well-being. We also offer support for many other mental health issues 
            to ensure personalized care for every individual.
          </p>
        </div>
      </section>

      {/* Meet The Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Meet The Team</h2>
          
          <div className="max-w-3xl mx-auto">
            {/* Team Description */}
            <div className="bg-gray-200 rounded-lg p-8">
              <p className="text-base text-gray-800 leading-relaxed mb-6">
                HealthCity's core team of its associates include qualified psychologists, therapists, nurses, doctors and other professionals, as 
                well as, former service users. They have relevant training, skills, and experience in mental health and physical wellbeing and its recovery. They work 
                with persons/patients or their families in the community to support their members. Behind the experienced directors and managers below 
                stands a number of associates, dedicated compassionate directors and advisors, clinicians, University researchers and client support staff 
                who make us all look good and achieving.
              </p>
              <div className="flex justify-center">
                <button 
                  onClick={() => navigate('/teams')}
                  className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                >
                  SEE ALL TEAM
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Are You Looking For? Section */}
      <section className="py-20 bg-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">What Are You Looking For?</h2>
          
          <div className="space-y-6">
            {assessmentQuestions.map((item, index) => (
              <div key={item.id} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <div className="flex gap-4">
                  <div className="text-2xl font-bold text-black opacity-20 shrink-0">
                    {String(item.id).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg text-white mb-4">{item.question}</p>
                    <div className="flex gap-4">
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        Yes
                      </button>
                      <button className="bg-gray-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors">
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-gray-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-500 transition-colors"
            >
              NEXT
            </button>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-300 leading-relaxed max-w-4xl mx-auto">
              Disclaimer: The questions and the questionnaires on this website do not give you a diagnosis. 
              You must ask to be assessed by and to ask for and use expert medical advice from a qualified 
              medical health professional if you are worried about your health or the health of someone else. 
              Healthcity and its affiliated organizations do not provide Emergency Services, as such the phone 
              numbers, emails and other contact details must not be used by you if you are risk to yourself 
              or to other people. If you are at risk or feel distressed, do contact your local emergency 
              services or your local family doctor for urgent advice and support. You can also find helplines 
              on the Keep Save/Risk Menus of this website.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-slate-700 text-white bg-cover bg-center relative" 
               style={{
                 backgroundImage: `url(${CTABackground})`
               }}>
        <div className="absolute inset-0 bg-slate-700/50"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Our Services Are Starting From As Low As £65
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button 
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-6 text-left"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{item.question}</h3>
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-400 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} 
                    />
                  </div>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What Our Clients Say About Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-200 rounded-lg p-8 relative">
                <div className="text-6xl text-gray-400 mb-4">"</div>
                <p className="text-gray-700 text-sm mb-6 leading-relaxed">
                  {testimonial.text}
                </p>
                <p className="text-gray-900 font-semibold text-sm">
                  – {testimonial.author}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-4 justify-start">
            <button className="bg-gray-400 hover:bg-gray-500 text-white p-3 rounded transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button className="bg-gray-400 hover:bg-gray-500 text-white p-3 rounded transition-colors">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section className="py-20 bg-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Book 1 - Thought Techniques */}
            <div className="relative h-[600px] flex items-start justify-center pt-12">
              {/* Book image - bottom layer, moved up 50% and left 50%, increased by 30% */}
              <div className="absolute -left-20 -top-12 w-80 h-[91%] rounded-lg shadow-2xl z-10 overflow-hidden">
                <img 
                  src={ThoughtsBookCover} 
                  alt="Thought Techniques for Simple Brain Files" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Green gradient content box - top layer, 50% opacity, centered, increased by 30% */}
              <div className="relative w-80 h-[97.5%] bg-gradient-to-br from-green-500/50 to-green-700/50 rounded-lg shadow-xl z-20 flex items-center justify-center px-8">
                <div className="text-white text-center">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
                    Thought Techniques for Simple Brain Files
                  </h3>
                  <button className="bg-white text-gray-900 px-8 py-3 rounded font-bold hover:bg-gray-100 transition-colors">
                    DOWNLOAD
                  </button>
                </div>
              </div>
            </div>

            {/* Book 2 - Twin Brain */}
            <div className="relative h-[600px] flex items-start justify-center pt-12">
              {/* Book image - bottom layer, moved up 50% and left 50%, increased by 30% */}
              <div className="absolute -left-20 -top-12 w-80 h-[91%] rounded-lg shadow-2xl z-10 overflow-hidden">
                <img 
                  src={TwinBrainBookCover} 
                  alt="Do I Have a Twin Brain?" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Blue gradient content box - top layer, 50% opacity, centered, increased by 30% */}
              <div className="relative w-80 h-[97.5%] bg-gradient-to-br from-blue-500/50 to-blue-700/50 rounded-lg shadow-xl z-20 flex items-center justify-center px-8">
                <div className="text-white text-center">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
                    Do I Have a Twin Brain?
                  </h3>
                  <button className="bg-white text-gray-900 px-8 py-3 rounded font-bold hover:bg-gray-100 transition-colors">
                    DOWNLOAD
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CBTServicesPage;